import { yupResolver } from '@hookform/resolvers/yup';
import { CircularProgress, Grid, Typography } from '@mui/material';
import { runStudyAlgorithm } from 'api/platform';
import { useTrackActionFormDirty } from 'components/ActionDialog/actionFormDirtyState';
import LabelledDropdown from 'components/atoms/Dropdown/LabelledDropdown';
import { find, first, isEmpty, size } from 'lodash';
import { useSnackbar } from 'notistack';
import React, { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { CasesParams } from 'utils/useCasesParams/types';
import { useMutationWithErrorSnackbar } from 'utils/useMutationWithErrorSnackbar';
import { useStudyAvailableAlgorithms } from 'utils/useStudyAvailableAlgorithms';
import * as yup from 'yup';
import { ActionStepper } from './ActionStepper';
import {
  buildStudyAlgorithmJobName,
  getStudyAlgorithmDisplayName,
  getStudyAlgorithmExecutionCopy,
  getStudyAlgorithmOptions,
  StudyAlgorithmExecutionCopy,
} from './studyAlgorithmExecution';
import { JobNameAndDescriptionStep } from './utils';

const SNACK_BAR_KEY_RUN_STUDY_ALGORITHM = 'RUN_STUDY_ALGORITHM';

const validationSchema = yup.object({
  algorithmId: yup.string().required('Must select an algorithm'),
  jobName: yup.string().required('Job Name is required'),
  jobDescription: yup.string().optional().nullable(),
});

const fieldsByStep: Array<Array<keyof StudyAlgorithmExecutionFormValues>> = [['algorithmId'], ['jobName']];

export interface StudyAlgorithmExecutionFormValues {
  jobName: string;
  jobDescription?: string;
  algorithmId: string;
}

export interface StudyAlgorithmExecutionFormProps {
  onClose: (event?: any, force?: boolean) => void;
  casesParams: CasesParams;
  copyOverrides?: Partial<StudyAlgorithmExecutionCopy>;
}

export const StudyAlgorithmExecutionForm = ({
  onClose,
  casesParams,
  copyOverrides,
}: StudyAlgorithmExecutionFormProps): ReactElement => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const activeStateHandlers = useState(0);
  const [activeStep, setActiveStep] = activeStateHandlers;
  const {
    register,
    handleSubmit,
    control,
    watch,
    trigger,
    formState: { errors, isDirty },
    setValue,
  } = useForm<StudyAlgorithmExecutionFormValues>({
    mode: 'onChange',
    resolver: yupResolver(validationSchema),
    defaultValues: {
      jobName: '',
      jobDescription: '',
      algorithmId: '',
    },
  });
  useTrackActionFormDirty(isDirty);

  const [isStepFailed, setIsStepFailed] = useState<Record<number, boolean>>({});
  const studyId = casesParams.studyId;
  const labId = casesParams.labId;
  const copy = useMemo(() => ({ ...getStudyAlgorithmExecutionCopy(), ...copyOverrides }), [copyOverrides]);

  const { data: availableAlgorithms, isLoading, isError } = useStudyAvailableAlgorithms(studyId);

  const candidateAlgorithms = useMemo(() => availableAlgorithms || [], [availableAlgorithms]);

  const currentAlgorithmId = watch('algorithmId');
  const currentAlgorithmEntry = find(candidateAlgorithms, { id: currentAlgorithmId });

  const checkValidationAndSetIsStepFailed = useCallback(
    async (stepIndex: number) => {
      const isValid = await trigger(fieldsByStep[stepIndex] || []);

      setIsStepFailed((prev) => ({
        ...prev,
        [stepIndex]: !isValid,
      }));

      return isValid;
    },
    [trigger]
  );

  const onAlgorithmIdSelect = useCallback(
    (algorithmId: string, skipJobName?: boolean) => {
      const selectedAlgorithm = find(candidateAlgorithms, { id: algorithmId });

      setValue('algorithmId', algorithmId);
      if (!skipJobName) {
        setValue('jobName', buildStudyAlgorithmJobName(selectedAlgorithm));
      }

      void checkValidationAndSetIsStepFailed(0);
      void checkValidationAndSetIsStepFailed(1);
    },
    [candidateAlgorithms, checkValidationAndSetIsStepFailed, setValue]
  );

  const hasCandidateAlgorithms = !isEmpty(candidateAlgorithms);
  const autoSelectedAlgorithm = size(candidateAlgorithms) === 1 ? first(candidateAlgorithms)?.id : undefined;

  useEffect(() => {
    if (autoSelectedAlgorithm && currentAlgorithmId !== autoSelectedAlgorithm) {
      onAlgorithmIdSelect(autoSelectedAlgorithm, isDirty);
      setActiveStep(1);
    }
  }, [autoSelectedAlgorithm, currentAlgorithmId, isDirty, onAlgorithmIdSelect, setActiveStep]);

  const runStudyAlgorithmMutation = useMutationWithErrorSnackbar({
    mutationFn: runStudyAlgorithm,
    mutationDescription: 'run an available study algorithm',
    onSuccess: () => {
      enqueueSnackbar(copy.startedText(getStudyAlgorithmDisplayName(currentAlgorithmEntry)), {
        variant: 'success',
      });
      onClose(undefined, true);
    },
    onSettled() {
      closeSnackbar(SNACK_BAR_KEY_RUN_STUDY_ALGORITHM);
    },
  });

  const onSubmit: SubmitHandler<StudyAlgorithmExecutionFormValues> = async (data) => {
    try {
      validationSchema.validateSync(data, { abortEarly: false });
    } catch (stepError) {
      console.error('Validation failed', stepError);
      enqueueSnackbar('Validation failed', { variant: 'error' });
      return;
    }

    if (!currentAlgorithmEntry) {
      throw Error('Failed to find selected algorithm');
    }

    enqueueSnackbar({
      variant: 'success',
      message: (
        <Grid container>
          <Grid item>
            <Typography>{copy.waitingForStartText(getStudyAlgorithmDisplayName(currentAlgorithmEntry))}</Typography>
          </Grid>
          <Grid item>
            <CircularProgress sx={{ marginLeft: 10 }} color="inherit" size={20} />
          </Grid>
        </Grid>
      ),
      key: SNACK_BAR_KEY_RUN_STUDY_ALGORITHM,
      autoHideDuration: null,
    });

    await runStudyAlgorithmMutation.mutateAsync({
      casesParams,
      studyId,
      labId,
      ...data,
    });
  };

  const options = useMemo(() => getStudyAlgorithmOptions(candidateAlgorithms), [candidateAlgorithms]);

  const steps = [
    {
      label: copy.selectionStepLabel,
      isLoading,
      subLabel: isLoading ? 'Loading...' : getStudyAlgorithmDisplayName(currentAlgorithmEntry),
      content: (
        <Grid container direction="column" spacing={2}>
          <Grid item>
            {isLoading || hasCandidateAlgorithms ? (
              <LabelledDropdown
                label={
                  <Grid item container columnSpacing={2}>
                    <Grid item>{copy.selectionInstruction}</Grid>
                    {isLoading && (
                      <Grid item>
                        <CircularProgress size={20} />
                      </Grid>
                    )}
                    {isError && (
                      <Grid item>
                        <Typography color="error">Failed to load algorithms</Typography>
                      </Grid>
                    )}
                    {errors.algorithmId?.message && (
                      <Grid item>
                        <Typography color="error">{errors.algorithmId.message}</Typography>
                      </Grid>
                    )}
                  </Grid>
                }
                options={options}
                value={currentAlgorithmId || ''}
                onOptionSelected={onAlgorithmIdSelect}
                error={Boolean(errors.algorithmId)}
              />
            ) : isError ? (
              <Typography color="error">Failed to load algorithms</Typography>
            ) : (
              <Typography>{copy.emptyStateText}</Typography>
            )}
          </Grid>
        </Grid>
      ),
      onNextOrBackClick: () => checkValidationAndSetIsStepFailed(0),
    },
    {
      label: 'Job Name and Description',
      subLabel: activeStep !== 1 && watch('jobName'),
      content: (
        <JobNameAndDescriptionStep
          control={control}
          errors={errors}
          register={register}
          onChangeName={() => {
            void checkValidationAndSetIsStepFailed(1);
          }}
        />
      ),
      onNextOrBackClick: () => checkValidationAndSetIsStepFailed(1),
    },
  ];

  return (
    <ActionStepper
      activeStateHandlers={activeStateHandlers}
      handleSubmit={handleSubmit(onSubmit)}
      steps={steps}
      setActiveStepForValidation={setActiveStep}
      isStepFailed={isStepFailed}
      validateStep={() => checkValidationAndSetIsStepFailed(activeStep)}
    />
  );
};
